/* -----------------Globals--------------- */
import { SectionList } from "react-native";

/* -----------------UI--------------- */
import { AlertStandard } from "@memorang/ui/src/components/AlertStandard";
import Box from "@memorang/ui/src/components/Box";

/* -----------------Helpers & Hooks--------------- */
import { statusMeta } from "../constants";

/* -----------------Types--------------- */
import type { PracticeTestsViewSection } from "../helpers/practice-tests-data";

import { getIsOlxExam, isOlxExamWithinDateRange } from "@helpers/exams";
import { useAppStore } from "@hooks/useAppStore";
import { useAppTheme } from "@hooks/useAppTheme";
import { useGlobalStore } from "@hooks/useGlobalStore";
import CustomListSubheaderContainer from "components/CustomListSubheaderContainer";
import type { EXAM_STATUS } from "types/tag";
import { getSummativeTestAlertConfig } from "../helpers/common";
/* -----------------Child components--------------- */
import { PracticeTestListItem } from "./PracticeTestListItem";
import { PremiumChip } from "./Progress/PremiumChip";

export const PracticeTestsView = ({
	sections,
	selectedExamId,
	handleNavigation,
	hasProductAccess,
}: {
	sections: PracticeTestsViewSection[];
	selectedExamId?: string;
	disableCard?: boolean;
	hasProductAccess?: boolean;
	handleNavigation: (
		id: string,
		status: EXAM_STATUS,
		numericId: number | undefined,
		isQuiz?: boolean,
		hasExamAccess?: boolean,
		canStartSession?: boolean,
		collectionId?: number,
	) => void;
}) => {
	const hasShownSummativeTestAlert = useGlobalStore(
		(state) => state.hasShownSummativeTestAlert,
	);
	const setHasShownSummativeTestAlert = useGlobalStore(
		(state) => state.setHasShownSummativeTestAlert,
	);
	const theme = useAppTheme();

	const summativeTestAlertConfig = useAppStore(
		(state) => state.exam.practiceTest.summativeTestAlertConfig,
	);

	const hasQuiz = sections.find((section) => section.type === "quiz");
	const hasPracticeTests = sections.find(
		(section) => section.type === "practice-tests",
	);

	const hasBothQuizAndPracticeTests = hasQuiz && hasPracticeTests;

	const renderSectionHeader = ({
		section: { title, rightChip },
	}: {
		section: PracticeTestsViewSection;
	}) => {
		return (
			<Box
				gap={16}
				backgroundColor={theme.colors.background}
				paddingHorizontal={16}
			>
				<Box
					justifyContent="space-between"
					flexDirection="row"
					alignItems="center"
				>
					<CustomListSubheaderContainer enableGutters={false}>
						{title}
					</CustomListSubheaderContainer>
					{rightChip && <PremiumChip {...rightChip} />}
				</Box>
			</Box>
		);
	};

	const renderItem = ({
		item,
	}: {
		item: PracticeTestsViewSection["data"][0];
	}) => {
		return (
			<Box gap={16}>
				{item.list[0].map((practiceTest) => {
					const {
						title,
						description,
						status,
						cta,
						numericId,
						collectionId,
						id,
						score,
						hasExamAccess,
						beginStartDate,
						endStartDate,
						subType,
						legacyReport,
						migratedSessionReport,
					} = practiceTest;
					const { text, icon } = statusMeta[status];
					const isFreeExam = subType === "DIAGNOSTIC";

					const isOlxExam = getIsOlxExam(id);

					const getCanStartSession = () => {
						if (isOlxExam) {
							if (status === "ACTIVE" || status === "COMPLETE") {
								return true;
							}
							return isOlxExamWithinDateRange(beginStartDate, endStartDate);
						}
						return true;
					};

					const canStartSession = getCanStartSession();

					return (
						<PracticeTestListItem
							key={id}
							label={title}
							description={description}
							text={text}
							isFreeExam={isFreeExam}
							hasAccess={hasExamAccess && hasProductAccess}
							icon={
								(hasExamAccess && hasProductAccess) ||
								isFreeExam ||
								legacyReport ||
								migratedSessionReport
									? icon!
									: "lock-outline"
							}
							status={status}
							cta={cta}
							loading={selectedExamId === id}
							score={score}
							handleNavigation={() =>
								handleNavigation(
									id,
									status,
									numericId,
									isFreeExam,
									hasExamAccess,
									canStartSession,
									collectionId,
								)
							}
						/>
					);
				})}
			</Box>
		);
	};

	const config = getSummativeTestAlertConfig({
		hasQuiz: !!hasQuiz,
		hasPracticeTests: !!hasPracticeTests,
		hasBothQuizAndPracticeTests: !!hasBothQuizAndPracticeTests,
		summativeTestAlertConfig,
	});

	const showAlert = !hasShownSummativeTestAlert && config;

	return (
		<Box gap={16} flex={1}>
			{showAlert && (
				<Box paddingHorizontal={16}>
					<AlertStandard
						description={config.description}
						severity={config.severity}
						icon={config.icon}
						action={{
							label: "Dismiss",
							onPress: () => setHasShownSummativeTestAlert(true),
						}}
						visible={!!showAlert}
						callback={() => setHasShownSummativeTestAlert(true)}
					/>
				</Box>
			)}
			<SectionList
				sections={sections}
				showsVerticalScrollIndicator={false}
				keyExtractor={(_, index) => index.toString()}
				renderItem={renderItem}
				renderSectionHeader={renderSectionHeader}
				SectionSeparatorComponent={() => <Box height={8} />}
				contentContainerStyle={{
					paddingBottom: 124,
					paddingHorizontal: 2,
				}}
			/>
		</Box>
	);
};
